import React, { useRef, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { ProgressIndicator } from "components/designV2/progressIndicator/progressIndicator";
import { TextArea } from "components/designV2/textarea/textarea";
import { useAlertStore } from "stores/alertStore/alertStore";
import { COMPANY_AUTH_PRIMARY_BUTTON_STYLES } from "web-apps/company/components/shared/companyAuth.buttonStyles.contract";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  ActionsRow,
  BackButton,
  ErrorText,
  FieldLabel,
  FieldsWrapper,
  FinishButton,
  HiddenFileInput,
  LoadingWrapper,
  ProfileIconCircle,
  ProfileImage,
  ProfileImageCircle,
  ProfilePlaceholderIcon,
  ProfileRow,
  StepLabel,
  StyledForm,
  StyledTitle,
  TextAreaWrapper,
  UploadButton,
} from "./createProfile.styled";

type Props = {
  companyBio: string;
  setCompanyBio: React.Dispatch<React.SetStateAction<string>>;
  updateCompanyDescription: () => Promise<void>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
};

export const CreateProfile: React.FC<Props> = ({
  companyBio,
  setCompanyBio,
  updateCompanyDescription,
  page,
  setPage,
}) => {
  const { t } = useTranslation();
  const [companyState, companyActions] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const [imageError, setImageError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputEl = useRef<HTMLInputElement>(null);

  const isProfileActionLoading =
    companyState.isLoading || companyState.imageIsLoading || isSubmitting;

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (isProfileActionLoading) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (companyBio) {
        await updateCompanyDescription();
      } else {
        setPage(page + 1);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateImage = async (): Promise<void> => {
    const file = inputEl.current?.files?.[0];
    const companyId = companyState.company?.id;

    if (!file || !companyId) {
      return;
    }

    if (file.size >= 20971520) {
      setImageError(true);
      return;
    }

    try {
      setImageError(false);
      await companyActions.updateCompanyImage(companyId, file);

      if (companyState.company?.id) {
        companyActions.getCompanyImage(companyState.company.id);
      }
    } catch {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("General.ErrorOccurred"),
        },
      });
    }
  };

  const buttonLabel =
    companyState.companyImage || companyBio
      ? t("RegisterCompany.CreateProfile")
      : t("General.Skip");

  return (
    <StyledForm onSubmit={handleSubmit}>
      <StepLabel>
        {t("RegisterCompany.StepOfTotal", { step: 3, total: 3 })}
      </StepLabel>
      <ProgressIndicator steps={3} currentStep={3} />
      <StyledTitle>{t("RegisterCompany.CreateProfile")}</StyledTitle>
      <FieldsWrapper>
        <FieldLabel>{t("RegisterCompany.ProfilePicture")}</FieldLabel>
        <ProfileRow>
          {companyState.imageIsLoading ? (
            <ProfileIconCircle>
              <LoadingWrapper>
                <CircularProgress color="inherit" />
              </LoadingWrapper>
            </ProfileIconCircle>
          ) : companyState.companyImage ? (
            <ProfileImageCircle>
              <ProfileImage
                src={companyState.companyImage}
                alt={t("RegisterCompany.ProfilePicture")}
              />
            </ProfileImageCircle>
          ) : (
            <ProfileIconCircle>
              <ProfilePlaceholderIcon />
            </ProfileIconCircle>
          )}
          <HiddenFileInput
            hidden
            type="file"
            id="upload-button"
            ref={inputEl}
            accept="image/png, image/jpeg"
            disabled={companyState.imageIsLoading}
            onChange={() => {
              updateImage();
            }}
          />
          <UploadButton
            type="button"
            label={t("ProfileWorker.AddProfilePicture")}
            onClick={() => inputEl.current?.click()}
            disabled={companyState.imageIsLoading}
          />
        </ProfileRow>
        {imageError && (
          <ErrorText>{t("ProfileCompany.FileTooLarge")}</ErrorText>
        )}
        <TextAreaWrapper>
          <TextArea
            label={t("RegisterCompany.Bio")}
            value={companyBio}
            placeholder={t("RegisterCompany.TellUsAboutCompany")}
            onChange={setCompanyBio}
            maxHeight={180}
          />
        </TextAreaWrapper>
      </FieldsWrapper>
      <ActionsRow>
        <BackButton
          type="button"
          label={t("General.Back")}
          onClick={() => setPage(page - 1)}
        />
        <FinishButton
          type="submit"
          label={buttonLabel}
          disabled={isProfileActionLoading}
          isLoading={isProfileActionLoading}
          styleOverrides={COMPANY_AUTH_PRIMARY_BUTTON_STYLES}
        />
      </ActionsRow>
    </StyledForm>
  );
};
